import { gql, useApolloClient, useMutation, useQuery } from "@apollo/client";
import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useHistory, useParams } from "react-router-dom";
import { Episode } from "../../components/episode";
import { SmallButton } from "../../components/small-button";
import { CATEGORY_FRAGMENT, EPISODE_FRAGMENT, PODCAST_FRAGMENT, REVIEW_FRAGMENT } from "../../fragments";
import { timeSince } from "../../util/utility";
import { MY_PODCASTS_QUERY } from "./my-podcasts";

export const MY_PODCAST_QUERY = gql`
  query myPodcastQuery ($input: MyPodcastInput!) {
    myPodcast (input: $input) {
      ok
      error
      podcast {
        ...PodcastParts
        category {
        ...CategoryParts
        }
        episodes {
        ...EpisodeParts
        }
        reviews {
        ...ReviewParts
        }
        creator {
            email
        }
      }
    }
  }
  ${PODCAST_FRAGMENT}
  ${CATEGORY_FRAGMENT}
  ${EPISODE_FRAGMENT}
  ${REVIEW_FRAGMENT}
`;


const DELETE_PODCAST_MUTATION = gql`
  mutation deletePodcast($input: PodcastSearchInput!) {
    deletePodcast(input: $input) {
      error
      ok
      id
    }
  }
`;

export const MyPodcast = () => {
    const history = useHistory();
    const client = useApolloClient();
    let { id } = useParams();
    id = Number(id);
    const [uploading, setUploading] = useState(false);

    const { data, loading } = useQuery<
        myPodcastQuery,
        myPodcastQueryVariables
    >(MY_PODCAST_QUERY, {
        variables: {
            input: {
                id: Number(id),
            },
        },
    });

    const onCompleted = (d: deletePodcast) => {
        const {
            deletePodcast: { ok, id },
        } = d;
        if (ok) {
            alert("Podcast Deleted!");
            setUploading(false);
            const queryResult = client.readQuery({ query: MY_PODCASTS_QUERY });
            const deletedList = queryResult.myPodcasts.podcasts.filter((podcast) => podcast.id !== id);

            client.writeQuery({
                query: MY_PODCASTS_QUERY,
                data: {
                    myPodcasts: {
                        ...queryResult.myPodcasts,
                        podcasts: deletedList,
                    },
                },
            });
            history.push("/");
        }
    };

    const [deletePodcastMutation, { }] = useMutation<
        deletePodcast,
        deletePodcastVariables
    >(DELETE_PODCAST_MUTATION, {
        onCompleted
    });

    const onDelete = async (id: number) => {
        try {
            setUploading(true);
            deletePodcastMutation({
                variables: {
                    input: {
                        id
                    },
                },
            });
        } catch (e) { }
    };

    return (
        <div className="pb-96">
            <Helmet>
                <title>My Podcast | KUDOK</title>
            </Helmet>
            <div className="bg-gray-800 w-full py-10 flex bg-no-repeat bg-cover" style={{ backgroundImage: `url(${data?.myPodcast.podcast?.coverImg || '/images/podcast.jpeg'})` }}>
                <div className="flex flex-col text-white p-5 bg-gradient-to-r from-black">
                    <div className="flex flex-row m-5">
                        <div className="flex flex-col">
                            <h3 className="text-5xl">{data?.myPodcast.podcast?.title}</h3>
                            <span className="py-2 text-xl border-gray-400">
                                {data?.myPodcast.podcast?.creator.email}
                            </span>
                            <span className="py-2 text-xs border-gray-400">
                                {timeSince(data?.myPodcast.podcast?.createdAt)}
                            </span>
                        </div>
                    </div>
                    <div className="flex flex-row m-5">
                        <h3 className="p-2 text-xl bg-cover bg-purple-900 rounded-lg">
                            Category 💫 : {data?.myPodcast.podcast?.category?.name}
                        </h3>
                        <h3 className="ml-5 p-2 text-xl bg-purple-900 rounded-lg">
                            Rating 💯 : {data?.myPodcast.podcast?.rating}
                        </h3>
                        <h3 className="ml-5 p-2 text-xl bg-purple-900 rounded-lg">
                            Listeners 👩🏻 : {data?.myPodcast.podcast?.subscribers?.length}
                        </h3>
                    </div>
                    <div className="p-5">
                        {data?.myPodcast.podcast?.description}
                    </div>
                    <div className="flex flex-row">
                        <SmallButton style="ml-5" actionText="Edit Podcast" onClick={() => {
                            history.push({
                                pathname: '/edit-podcast',
                                state: { podcast: data?.myPodcast.podcast }
                            })
                        }} />
                        <SmallButton style="ml-5" loading={loading} actionText="Delete Podcast" onClick={() => onDelete(id)} />
                    </div>
                </div>
            </div>
            <h3 className="py-5 pl-5 text-xl bg-purple-500">
                {data?.myPodcast.podcast?.episodes?.length} Episodes 🎬
                <SmallButton style="float-right mr-5" actionText="Upload Episode" onClick={() => {
                    history.push({
                        pathname: '/upload-episode',
                        state: { podcast: data?.myPodcast.podcast }
                    })
                }} />
            </h3>
            {!loading && (
                <div className="max-w-screen-2xl mx-auto mt-8 pb-20">
                    <div className="grid grid-cols-1 gap-x-5 gap-y-4">
                        {data?.myPodcast.podcast?.episodes?.map((episode) => (
                            <Episode
                                id={episode.id + ""}
                                title={episode.title}
                                description={episode.description}
                                createdAt={episode.createdAt}
                                state={episode}
                            />
                        ))}
                    </div>
                </div>
            )}
            <h3 className="py-5 pl-5 text-xl bg-purple-500">
                {data?.myPodcast.podcast?.reviews?.length} Reviews 📝
            </h3>
            <div className="max-w-screen-2xl mx-auto mt-8 pb-20">
                <div className="grid grid-cols-1 gap-x-5 gap-y-4">
                    {data?.myPodcast.podcast?.reviews?.map((review) => (
                        <div key={review.id} className="flex flex-row m-3 gap-3 pb-5 border-b border-gray-40">
                            <div>
                                <div className="text-purple-500 font-bold">{review.title}</div>
                                <div>{review.text}</div>
                            </div>

                            <div>
                                {/* <span className="text-purple">{review.creator?.email}</span> */}
                                <span>{timeSince(review.createdAt)}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};